/**
 * `<TimelineEntry/>` — one row of the per-task timeline rail: a coloured dot
 * with the event glyph on a vertical rail, then the factual phrase, an optional
 * state pill, an optional "on «sub-task»" attribution (subtree scope), and a
 * quiet `timestamp · by Actor` line.
 *
 * The canonical reading unit, shared by the compact drawer timeline and the
 * full-panel timeline (one row implementation, never forked). Presentational:
 * the phrase comes pre-described (via `describeEvent`) from the engine; this
 * component only paints it.
 *
 * Brand firewall (§A9): the row is provenance about *the task* — what changed,
 * when, and (quietly) by whom. The actor is attribution, never a tally. The
 * optional node attribution names the *sub-task* an event belongs to (the
 * work), never a person.
 *
 * Editorial re-skin (#1043, Wave 2): theme-aware. EDITORIAL (`.t-history .ev`):
 * a state-tinted vertical rail behind a quiet `.ic` icon circle, a 13.5px
 * title, a mono `time · by whom` meta line, and (when the row carries a
 * destination state) a mono `while <state>` annotation. LEGACY keeps the
 * shipped dot-on-a-connecting-line rail, byte-identical — the structure +
 * every `data-testid` is shared, the look forks on the resolved `theme`.
 */

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import type { DescribedEvent } from "./describeEvent";
import { EventIcon, eventVisual } from "./eventIcon";
import { formatEventStamp } from "./formatTime";

export interface TimelineEntryProps {
  /** The event type — drives the glyph. */
  readonly eventType: string;
  /** ISO instant — rendered as "May 18 · 09:14" in the team tz. */
  readonly occurredAt: string;
  /** The pre-described event (phrase + destination state + accent). */
  readonly described: DescribedEvent;
  /** The actor, resolved by the caller; undefined → neutral "—" avatar. */
  readonly actor: TaskMember | undefined;
  /**
   * The sub-task this event belongs to, when it is NOT the focal node (subtree
   * scope). Shown as a quiet "on «title»" tag so a mixed stream stays legible.
   * Omit / null for the focal node's own events.
   */
  readonly nodeLabel?: string | null;
  /** Tighter padding + type for the 400px drawer. */
  readonly compact?: boolean;
  /** When true, the rail's connecting line below the dot is omitted (day tail). */
  readonly lastInDay?: boolean;
  /** Team IANA zone for the timestamp; omit for the viewer's zone. */
  readonly tz?: string | undefined;
}

export function TimelineEntry({
  eventType,
  occurredAt,
  described,
  actor,
  nodeLabel = null,
  compact = false,
  lastInDay = false,
  tz,
}: TimelineEntryProps) {
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const visual = eventVisual(eventType, described.toState, theme);

  if (editorial) {
    return (
      <EditorialEntry
        eventType={eventType}
        occurredAt={occurredAt}
        described={described}
        actor={actor}
        nodeLabel={nodeLabel}
        compact={compact}
        tz={tz}
        visual={visual}
        theme={theme}
      />
    );
  }

  const dotSize = compact ? 22 : 26;

  return (
    <li
      data-testid="timeline-entry"
      data-event-type={eventType}
      className="flex items-start"
      style={{ gap: compact ? 10 : 14 }}
    >
      {/* Rail column: the dot, with a connecting line beneath (unless last). */}
      <div className="relative flex shrink-0 flex-col items-center" style={{ width: dotSize }}>
        <span
          data-testid="timeline-entry-dot"
          data-state={described.toState ?? undefined}
          className="relative z-[1] flex items-center justify-center rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            backgroundColor: visual.bg,
            color: visual.fg,
            border: `1.4px solid ${visual.fg}`,
          }}
        >
          <EventIcon type={eventType} size={compact ? 13 : 15} />
        </span>
        {!lastInDay && (
          <span
            aria-hidden
            className="absolute left-1/2 w-0 -translate-x-1/2"
            style={{
              top: dotSize,
              bottom: compact ? -10 : -14,
              borderLeft: `1.4px dashed ${PALETTE.connector}`,
            }}
          />
        )}
      </div>

      {/* Content. */}
      <div className="min-w-0 flex-1" style={{ paddingBottom: compact ? 10 : 14 }}>
        <div className="leading-snug" style={{ color: PALETTE.ink, fontSize: compact ? 13 : 14 }}>
          {described.toState && (
            <span
              data-testid="timeline-state-pill"
              className="mr-1.5 inline-block rounded-full px-1.5 font-semibold align-[1px]"
              style={{
                backgroundColor: described.accent ? `${described.accent}1A` : undefined,
                color: described.accent ?? undefined,
                border: described.accent ? `1px solid ${described.accent}55` : undefined,
                fontSize: compact ? 11 : 12,
              }}
            >
              {described.phrase}
            </span>
          )}
          {!described.toState && (
            <span data-testid="timeline-phrase" style={{ color: PALETTE.ink }}>
              {described.phrase}
            </span>
          )}
          {nodeLabel && (
            <span data-testid="timeline-node-tag" style={{ color: PALETTE.muted }}>
              {" "}
              on <span style={{ color: PALETTE.slate }}>“{nodeLabel}”</span>
            </span>
          )}
        </div>
        <div
          className="mt-0.5 flex items-center gap-1.5 font-mono"
          style={{ color: PALETTE.muted, fontSize: compact ? 10.5 : 11 }}
        >
          <span data-testid="timeline-stamp">{formatEventStamp(occurredAt, tz)}</span>
          <span aria-hidden style={{ opacity: 0.5 }}>
            ·
          </span>
          <ActorTag actor={actor} />
        </div>
      </div>
    </li>
  );
}

// ── Editorial row (#1043, Wave 2) ────────────────────────────────────────────

/**
 * The editorial `.t-history .ev` row: a state-tinted vertical rail behind a
 * quiet `.ic` icon circle (3 columns: icon · content · `while` annotation).
 * The rail tint reuses the row's destination-state colour (`theme.stateStyle`'s
 * saturated `dot`); a row with no destination state (a rename, a note) takes a
 * neutral rule rail. The `while <state>` annotation reads only when the row
 * itself carries a state move — never a recomputed projection, never a person.
 */
function EditorialEntry({
  eventType,
  occurredAt,
  described,
  actor,
  nodeLabel,
  compact,
  tz,
  visual,
  theme,
}: {
  eventType: string;
  occurredAt: string;
  described: DescribedEvent;
  actor: TaskMember | undefined;
  nodeLabel: string | null;
  compact: boolean;
  tz: string | undefined;
  visual: { fg: string; bg: string };
  theme: ReturnType<typeof useMapTheme>;
}) {
  const c = theme.palette;
  // The state-tinted rail behind the icon (the wireframe `--st-rail` idiom):
  // the destination state's saturated colour, else a neutral hairline.
  const railColor = described.toState
    ? (theme.stateStyle(described.toState).dot ?? visual.fg)
    : c.rule;
  const icSize = compact ? 24 : 28;

  return (
    <li
      data-testid="timeline-entry"
      data-event-type={eventType}
      className="relative grid items-start"
      style={{
        gridTemplateColumns: `${icSize}px 1fr auto`,
        gap: compact ? 10 : 14,
        paddingTop: compact ? 10 : 14,
        paddingBottom: compact ? 10 : 14,
      }}
    >
      {/* The state-tinted vertical rail behind the icon column. */}
      <span
        aria-hidden
        data-testid="timeline-entry-rail"
        className="absolute top-0 bottom-0"
        style={{ left: icSize / 2 - 1.5, width: 3, backgroundColor: railColor }}
      />

      {/* The `.ic` icon circle. */}
      <span
        data-testid="timeline-entry-dot"
        data-state={described.toState ?? undefined}
        className="relative z-[1] flex items-center justify-center rounded-full"
        style={{
          width: icSize,
          height: icSize,
          backgroundColor: visual.bg,
          color: visual.fg,
          border: `1px solid ${visual.fg}`,
        }}
      >
        <EventIcon type={eventType} size={compact ? 13 : 15} />
      </span>

      {/* Content: a 13.5px title, then a mono `time · by whom` meta line. */}
      <div className="min-w-0">
        <div className="leading-snug" style={{ color: c.ink, fontSize: compact ? 13 : 13.5 }}>
          {described.toState && (
            <span
              data-testid="timeline-state-pill"
              className="mr-1.5 inline-block rounded-full px-1.5 font-semibold align-[1px]"
              style={{
                backgroundColor: theme.stateStyle(described.toState).chipBg,
                color: theme.stateStyle(described.toState).fg,
                fontSize: compact ? 11 : 12,
                fontFamily: theme.fonts.sans,
              }}
            >
              {described.phrase}
            </span>
          )}
          {!described.toState && (
            <span data-testid="timeline-phrase" style={{ color: c.ink }}>
              {described.phrase}
            </span>
          )}
          {nodeLabel && (
            <span data-testid="timeline-node-tag" style={{ color: c.muted }}>
              {" "}
              on <span style={{ color: c.slate }}>“{nodeLabel}”</span>
            </span>
          )}
        </div>
        <div
          className="mt-1 flex items-center gap-1.5"
          style={{
            color: c.muted,
            fontSize: compact ? 10.5 : 11,
            fontFamily: theme.fonts.mono,
            letterSpacing: "0.02em",
          }}
        >
          <span data-testid="timeline-stamp">{formatEventStamp(occurredAt, tz)}</span>
          <span aria-hidden style={{ opacity: 0.5 }}>
            ·
          </span>
          <ActorTag actor={actor} />
        </div>
      </div>

      {/* The mono `while <state>` annotation — only for a state move (never a
          recomputed projection); a left rule tinted to the rail colour. */}
      {described.toState ? (
        <div
          data-testid="timeline-while"
          className="self-stretch whitespace-nowrap pl-2"
          style={{
            color: c.muted,
            fontSize: 10.5,
            fontFamily: theme.fonts.mono,
            letterSpacing: "0.02em",
            borderLeft: `2px solid ${railColor}`,
            paddingTop: compact ? 2 : 4,
          }}
        >
          while {theme.stateStyle(described.toState).label.toLowerCase()}
        </div>
      ) : (
        <span aria-hidden />
      )}
    </li>
  );
}

/**
 * The quiet actor attribution: initials + name. A null / unknown actor (a
 * hard-deleted member, FK SET NULL) degrades to "a teammate" — never a crash,
 * never a leak, never a count (§A9).
 */
function ActorTag({ actor }: { actor: TaskMember | undefined }) {
  if (!actor) {
    return (
      <span data-testid="timeline-actor-unknown" style={{ opacity: 0.85 }}>
        by a teammate
      </span>
    );
  }
  return (
    <span data-testid="timeline-actor" style={{ opacity: 0.85 }} title={actor.name}>
      by {actor.name}
    </span>
  );
}
